<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useArrowsAiAssistants } from 'dashboard/composables/useArrowsAiAssistants';
import { useArrowsAiDocuments } from 'dashboard/composables/useArrowsAiDocuments';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import PageLayout from 'dashboard/components-next/arrowsAi/PageLayout.vue';
import EmptyState from 'dashboard/components-next/arrowsAi/EmptyState.vue';
import MissingAssistant from 'dashboard/components-next/arrowsAi/MissingAssistant.vue';
import BulkSelectBar from 'dashboard/components-next/arrowsAi/assistant/BulkSelectBar.vue';
import DocumentCard from 'dashboard/components-next/arrowsAi/assistant/DocumentCard.vue';
import DocumentDropzone from 'dashboard/components-next/arrowsAi/assistant/DocumentDropzone.vue';
import DocumentEditDialog from 'dashboard/components-next/arrowsAi/assistant/DocumentEditDialog.vue';

const { t } = useI18n();
const route = useRoute();
const { getAssistant, rememberAssistant } = useArrowsAiAssistants();
const assistantId = computed(() => Number(route.params.assistantId));
const assistant = computed(() => {
  const current = getAssistant(assistantId.value);
  if (current) rememberAssistant(current.id);
  return current;
});
const {
  documents,
  isLoading,
  addDocuments,
  deleteDocuments,
  downloadDocument,
  documentContent,
  saveDocumentText,
} = useArrowsAiDocuments(assistantId);
const selectedIds = ref([]);
const selectedLabel = computed(() =>
  t('ARROWS_AI.DOCUMENTS.SELECTED', { count: selectedIds.value.length })
);
const dialogRef = ref(null);
const editDialogRef = ref(null);
const isEmpty = computed(() =>
  Boolean(assistant.value && !isLoading.value && !documents.value.length)
);
const ITEMS_PER_PAGE = 25;
const currentPage = ref(1);
const pagedDocuments = computed(() => {
  const start = (currentPage.value - 1) * ITEMS_PER_PAGE;
  return documents.value.slice(start, start + ITEMS_PER_PAGE);
});

watch(
  () => documents.value.length,
  total => {
    const maxPage = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));
    if (currentPage.value > maxPage) currentPage.value = maxPage;
  }
);

const openDialog = () => dialogRef.value.open();

const addedNames = files => {
  const names = files.map(file => file.name);
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 3).join(', ')} +${names.length - 3}`;
};

const handleAdd = async files => {
  try {
    await addDocuments(files);
    dialogRef.value.close();
    await nextTick();
    useAlert(t('ARROWS_AI.DOCUMENTS.SUCCESS', { name: addedNames(files) }));
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.DOCUMENTS.SAVE_ERROR'));
  }
};

const toggleSelect = id => {
  selectedIds.value = selectedIds.value.includes(id)
    ? selectedIds.value.filter(item => item !== id)
    : [...selectedIds.value, id];
};

const removeDocuments = async ids => {
  try {
    await deleteDocuments(ids);
    selectedIds.value = selectedIds.value.filter(id => !ids.includes(id));
    useAlert(t('ARROWS_AI.DOCUMENTS.DELETED'));
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.DOCUMENTS.DELETE_ERROR'));
  }
};

const handleDownload = async document => {
  try {
    await downloadDocument(document);
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('ARROWS_AI.DOCUMENTS.DOWNLOAD_ERROR')
    );
  }
};
</script>

<template>
  <PageLayout
    :header-title="t('ARROWS_AI.DOCUMENTS.TITLE')"
    :button-label="t('ARROWS_AI.DOCUMENTS.ADD')"
    :is-empty="isEmpty"
    :show-pagination-footer="Boolean(assistant)"
    :current-page="currentPage"
    :total-count="documents.length"
    :items-per-page="ITEMS_PER_PAGE"
    @click="openDialog"
    @update:current-page="currentPage = $event"
  >
    <template #emptyState>
      <EmptyState
        :title="t('ARROWS_AI.DOCUMENTS.EMPTY_TITLE')"
        :subtitle="t('ARROWS_AI.DOCUMENTS.EMPTY_SUBTITLE')"
        :action-label="t('ARROWS_AI.DOCUMENTS.ADD')"
        @click="openDialog"
      />
    </template>
    <MissingAssistant v-if="!assistant" />
    <div v-else class="flex flex-col gap-4">
      <BulkSelectBar
        v-model="selectedIds"
        :items="pagedDocuments"
        :select-all-label="t('ARROWS_AI.DOCUMENTS.SELECT_ALL')"
        :unselect-all-label="t('ARROWS_AI.DOCUMENTS.UNSELECT_ALL')"
        :selected-label="selectedLabel"
        :delete-label="t('ARROWS_AI.DOCUMENTS.DELETE')"
        @bulk-delete="removeDocuments(selectedIds)"
      />
      <DocumentCard
        v-for="document in pagedDocuments"
        :key="document.id"
        :document="document"
        :selected="selectedIds.includes(document.id)"
        @select="toggleSelect"
        @delete="removeDocuments([$event])"
        @download="handleDownload"
        @edit="editDialogRef.open"
      />
    </div>
  </PageLayout>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('ARROWS_AI.DOCUMENTS.ADD')"
    :description="t('ARROWS_AI.DOCUMENTS.ADD_DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <DocumentDropzone @add="handleAdd" />
    <template #footer />
  </Dialog>
  <DocumentEditDialog
    ref="editDialogRef"
    :load-content="documentContent"
    :save-content="saveDocumentText"
  />
</template>
